// Baked light of a map: the decoded volume as four 3D textures plus the uniforms the surface shader reads.
// One instance per built map; `patch` hooks a material into it, `setLevel` / `setDebug` are the runtime controls.

import * as THREE from "three";
import { decodeBake, sampleBake } from "./bakedFormat";
import type { BakeSample, BakeVolume } from "./bakedFormat";
import { applyBakedSurface } from "./materials/surfaceShader";
import type { BakeUniforms } from "./materials/surfaceShader";
import type { MapBake, MapEnv, MapFileJson, MapVec3 } from "./mapTypes";

/** What the surface shader evaluates: 0 plain hemisphere lighting, 1 occlusion + sky visibility, 2 also bounce light and baked lamps. */
export type BakeLevel = 0 | 1 | 2;

export const BAKE_DEBUG_VIEWS = ["off", "occlusion", "sky", "bounce", "lamps"] as const;
export type BakeDebugView = (typeof BAKE_DEBUG_VIEWS)[number];

/** Defaults of the shader parameters (see BakeUniforms in surfaceShader.ts). */
export const BAKE_DEFAULTS = {
  /** Metres along the surface normal at which the volume is sampled. */
  offset: 0.5,
  /** Exponent of the baked occlusion (1 = as baked, larger = darker corners, 0 = off). */
  occlusion: 2,
  /** Exponent of the sky visibility that darkens the sky share of the hemisphere light (0 = off). */
  sky: 1,
  /** Multiplier of the bounce light and of the baked lamps. */
  bounce: 1,
  lamps: 1,
  /** Multiplier of the dirt, edge wear and roughness response (0 = off). */
  material: 1,
} as const;

export type BakeStrengths = { [K in keyof typeof BAKE_DEFAULTS]: number };

function volumeTexture(data: Uint8Array, dims: MapVec3): THREE.Data3DTexture {
  const t = new THREE.Data3DTexture(data, dims[0], dims[1], dims[2]);
  t.format = THREE.RGBAFormat;
  t.type = THREE.UnsignedByteType;
  t.minFilter = THREE.LinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.wrapS = THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  t.wrapR = THREE.ClampToEdgeWrapping;
  t.generateMipmaps = false;
  t.unpackAlignment = 1;
  t.needsUpdate = true;
  return t;
}

export class BakedLight {
  readonly volume: BakeVolume;
  /** GPU memory of the four textures. */
  readonly gpuBytes: number;
  private readonly uniforms: BakeUniforms;
  private readonly textures: THREE.Data3DTexture[];
  private level: BakeLevel = 2;

  private constructor(volume: BakeVolume, env: Pick<MapEnv, "sunColor" | "sunIntensity">) {
    this.volume = volume;
    const occ = volumeTexture(volume.occ, volume.dims);
    const sky = volumeTexture(volume.sky, volume.coarseDims);
    const bounce = volumeTexture(volume.bounce, volume.coarseDims);
    const tint = volumeTexture(volume.tint, volume.coarseDims);
    this.textures = [occ, sky, bounce, tint];
    this.gpuBytes = volume.occ.length + volume.sky.length + volume.bounce.length + volume.tint.length;
    const extent = new THREE.Vector3(volume.dims[0], volume.dims[1], volume.dims[2]).multiplyScalar(volume.cell);
    const [tintA, tintB] = volume.lampTints;
    this.uniforms = {
      uBakeOcc: { value: occ },
      uBakeSky: { value: sky },
      uBakeBounce: { value: bounce },
      uBakeTint: { value: tint },
      uBakeLampTints: { value: [new THREE.Vector3(...tintA), new THREE.Vector3(...tintB)] },
      uBakeOrigin: { value: new THREE.Vector3(volume.origin[0], volume.origin[1], volume.origin[2]) },
      uBakeInvSize: { value: new THREE.Vector3(1 / extent.x, 1 / extent.y, 1 / extent.z) },
      uBakeScale: { value: new THREE.Vector2(volume.bounceScale, volume.lampScale) },
      uBakeSun: { value: new THREE.Vector3() },
      uBakeParams: { value: new THREE.Vector4(this.level, BAKE_DEFAULTS.offset, BAKE_DEFAULTS.occlusion, BAKE_DEFAULTS.sky) },
      uBakeMix: { value: new THREE.Vector4(BAKE_DEFAULTS.bounce, BAKE_DEFAULTS.lamps, 0, BAKE_DEFAULTS.material) },
    };
    this.setSun(env.sunColor, env.sunIntensity);
  }

  /** Decodes `bake` (async, the inflate runs off the main thread) and creates the textures. */
  static async create(bake: MapBake, env: Pick<MapEnv, "sunColor" | "sunIntensity">): Promise<BakedLight> {
    return new BakedLight(await decodeBake(bake), env);
  }

  /** Hooks a map surface material into the baked light; every patched material shares one shader program. */
  patch(material: THREE.MeshStandardMaterial, grime: number): void {
    applyBakedSurface(material, this.uniforms, grime);
  }

  setLevel(level: BakeLevel): void {
    this.level = level;
    this.uniforms.uBakeParams.value.x = level;
  }

  get currentLevel(): BakeLevel {
    return this.level;
  }

  /** The bounce light is relative to the sun: call when the sun colour or intensity changes. */
  setSun(color: THREE.ColorRepresentation, intensity: number): void {
    const c = new THREE.Color(color);
    this.uniforms.uBakeSun.value.set(c.r * intensity, c.g * intensity, c.b * intensity);
  }

  setStrengths(s: Partial<BakeStrengths>): void {
    const p = this.uniforms.uBakeParams.value;
    const m = this.uniforms.uBakeMix.value;
    if (s.offset !== undefined) p.y = s.offset;
    if (s.occlusion !== undefined) p.z = s.occlusion;
    if (s.sky !== undefined) p.w = s.sky;
    if (s.bounce !== undefined) m.x = s.bounce;
    if (s.lamps !== undefined) m.y = s.lamps;
    if (s.material !== undefined) m.w = s.material;
  }

  /** Shows one baked channel instead of the lit surface (verification aid). */
  setDebug(view: BakeDebugView): void {
    this.uniforms.uBakeMix.value.z = BAKE_DEBUG_VIEWS.indexOf(view);
  }

  /** CPU sample for objects that are not map surfaces (same filtering as the shader). */
  sample(x: number, y: number, z: number, nx: number, ny: number, nz: number, out: BakeSample): void {
    sampleBake(this.volume, x, y, z, nx, ny, nz, this.uniforms.uBakeParams.value.y, out);
  }

  dispose(): void {
    for (const t of this.textures) t.dispose();
  }
}

/** The baked light of `map`, or null when it has none or its data is malformed (logged, the map then renders unbaked). */
export async function loadBakedLight(map: MapFileJson): Promise<BakedLight | null> {
  if (map.bake === undefined) return null;
  try {
    return await BakedLight.create(map.bake, map.env);
  } catch (err) {
    console.warn(`bakedLight: map "${map.id}" has an unusable bake, rendering without it`, err);
    return null;
  }
}
